import { useMemo } from 'react';
import { useQueryEvidenceIntegrations } from '@/hooks/query/integrations/evidence/useQueryEvidenceIntegrations';
import {
  mapIntegrationToPartnerConfig,
  type IntegrationPartnerConfig,
} from '@/components/chat/constants/integrationPartners';

/**
 * Returns integration partners that are available for the user's region.
 *
 * Fetches from the backend endpoint and filters to `is_available: true`,
 * then maps to the IntegrationPartnerConfig shape used by UI components.
 *
 * Partners with `requires_setup: true` and no `setup_config` are annotated
 * with `needsSetup: true` so the UI shows a setup flow instead of a toggle.
 */
export function useAvailableIntegrations() {
  const { data, isLoading } = useQueryEvidenceIntegrations();

  const partners: IntegrationPartnerConfig[] = useMemo(() => {
    if (!data?.integrations) return [];
    return data.integrations
      .filter((i) => i.is_available)
      .map((i) => {
        const config = mapIntegrationToPartnerConfig(i);
        if (i.requires_setup && !i.setup_config) {
          return { ...config, needsSetup: true };
        }
        return config;
      });
  }, [data]);

  return { partners, isLoading };
}
